import { useIsFocused } from '@react-navigation/native';
import {
    InfiniteData,
    QueryClient,
    QueryKey,
    UseInfiniteQueryResult,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import { z } from 'zod';
import safeParse from '_utils/safeParse';
import {
    BookingSharingAvailability,
    BookingSharingAvailabilitySchema,
    BookingSharingRequest,
    BookingSharingRequestSchema,
} from 'types/BookingSharing';
import { useLaravelInfinteQuery } from './utility';
import { useGetAccessTokenHeader } from '../_utils/Axios';
import { useSelectedCoop } from '../SelectedCoop';
import { LaravelPagingResponse } from '../types/Utility';

const parkingSharingRequestsMultipleKey = 'parkingSharingsRequests';
const parkingSharingRequestsSingleKey = 'parkingSharingRequest';
const parkingSharingAvailabilitiesMultipleKey = 'parkingSharingsAvailabilities';
const parkingSharingAvailabilitiesSingleKey = 'parkingSharingAvailability';

export const invalidateParkingSharingQueries = (queryClient: QueryClient): void => {
    queryClient.invalidateQueries({
        queryKey: [parkingSharingRequestsMultipleKey],
    });
    queryClient.invalidateQueries({
        queryKey: [parkingSharingAvailabilitiesMultipleKey],
    });
    queryClient.invalidateQueries({
        queryKey: [parkingSharingRequestsSingleKey],
    });
    queryClient.invalidateQueries({
        queryKey: [parkingSharingAvailabilitiesSingleKey],
    });
};

const parkingSharingRequestQueryKey = (cooperativeId: number) => [parkingSharingRequestsMultipleKey, cooperativeId];

const parkingSharingRequestArraySchema = z.array(BookingSharingRequestSchema);

const useGetParkingSharingRequests = (): UseInfiniteQueryResult<
    InfiniteData<BookingSharingRequest[]>,
    string | Error
> => {
    const getAuthHeader = useGetAccessTokenHeader();
    const selectedCoopId = useSelectedCoop();
    const isFocused = useIsFocused();
    return useLaravelInfinteQuery(
        parkingSharingRequestQueryKey(selectedCoopId),
        async ({ pageParam = 1 }) =>
            await axios.get(`cooperatives/${selectedCoopId}/parking_sharing/availability_requests`, {
                headers: { authorization: await getAuthHeader() },
                params: {
                    page: pageParam,
                },
            }),
        {
            gcTime: 1000 * 60 * 60 * 24,
            staleTime: 1000 * 60 * 1,
            enabled: isFocused,
        },
        parkingSharingRequestArraySchema,
    );
};

const getItemFromCollectionData = <ItemType extends { id: string }>(
    queryClient: QueryClient,
    queryPredicate: (query: QueryKey) => boolean,
    itemId: string,
) => {
    const relatedQueries = queryClient.getQueriesData<InfiniteData<LaravelPagingResponse<ItemType[]>>>({
        predicate: (query) => queryPredicate(query.queryKey),
    });

    let result: ItemType | undefined;
    relatedQueries.forEach((query) => {
        query[1]?.pages?.forEach((page) => {
            page.data?.forEach((item: ItemType) => {
                if (itemId === item.id) {
                    result = item;
                }
            });
        });
    });
    return result;
};

const deletedItemSchema = z.object({
    status: z.literal('deleted'),
});
type DeletedItem = z.infer<typeof deletedItemSchema>;
const parkingSharingRequestSchema = z.union([BookingSharingRequestSchema, deletedItemSchema]);

const useGetParkingSharingRequest = (
    requestId: string,
): UseQueryResult<BookingSharingRequest | DeletedItem, string | Error> => {
    const isFocused = useIsFocused();
    const getAuthHeader = useGetAccessTokenHeader();
    const selectedCoopId = useSelectedCoop();
    const queryClient = useQueryClient();

    return useQuery({
        queryKey: [parkingSharingRequestsSingleKey, selectedCoopId, requestId],

        queryFn: async () => {
            const result = await axios.get<unknown>(
                `cooperatives/${selectedCoopId}/parking_sharing/availability_requests/${requestId}`,
                {
                    headers: { authorization: await getAuthHeader() },
                },
            );

            if (!result.data) {
                throw new Error('Result returned with no data');
            }

            return safeParse(result.data, parkingSharingRequestSchema);
        },

        gcTime: 1000 * 60 * 60,
        staleTime: 1000 * 60 * 1,

        initialData: () =>
            getItemFromCollectionData<BookingSharingRequest>(
                queryClient,
                (key) => {
                    const [base, cooperative_id] = parkingSharingRequestQueryKey(selectedCoopId);
                    return base === key[0] && cooperative_id === key[1];
                },
                requestId,
            ),
        initialDataUpdatedAt: moment().subtract({ minutes: 2 }).valueOf(),
        enabled: isFocused && !!requestId,
    });
};

const parkingSharingAvailabilityQueryKey = (cooperativeId: number) => [
    parkingSharingAvailabilitiesMultipleKey,
    cooperativeId,
];

const parkingSharingAvailabilityArraySchema = z.array(BookingSharingAvailabilitySchema);
const useGetParkingSharingAvailabilities = (): UseInfiniteQueryResult<
    InfiniteData<BookingSharingAvailability[]>,
    string | Error
> => {
    const isFocused = useIsFocused();
    const getAuthHeader = useGetAccessTokenHeader();
    const selectedCoopId = useSelectedCoop();

    return useLaravelInfinteQuery(
        parkingSharingAvailabilityQueryKey(selectedCoopId),
        async ({ pageParam = 1 }) => {
            return await axios.get(`cooperatives/${selectedCoopId}/parking_sharing/availability_slots`, {
                headers: { authorization: await getAuthHeader() },
                params: {
                    page: pageParam,
                },
            });
        },
        {
            gcTime: 1000 * 60 * 60 * 24,
            staleTime: 1000 * 60 * 1,
            enabled: isFocused,
        },
        parkingSharingAvailabilityArraySchema,
    );
};

const parkingSharingAvailabilitySchema = z.union([BookingSharingAvailabilitySchema, deletedItemSchema]);

const useGetParkingSharingAvailability = (
    availabilityId: string,
): UseQueryResult<BookingSharingAvailability | DeletedItem, string | Error> => {
    const isFocused = useIsFocused();
    const getAuthHeader = useGetAccessTokenHeader();
    const selectedCoopId = useSelectedCoop();
    const queryClient = useQueryClient();

    return useQuery({
        queryKey: [parkingSharingAvailabilitiesSingleKey, selectedCoopId, availabilityId],
        queryFn: async () => {
            const result = await axios.get<BookingSharingAvailability>(
                `cooperatives/${selectedCoopId}/parking_sharing/availability_slots/${availabilityId}`,
                {
                    headers: { authorization: await getAuthHeader() },
                },
            );

            if (!result.data) {
                throw new Error('Result returned with no data');
            }

            return safeParse(result.data, parkingSharingAvailabilitySchema);
        },
        initialData: () =>
            getItemFromCollectionData<BookingSharingAvailability>(
                queryClient,
                (key) => {
                    const [base, cooperative_id] = parkingSharingAvailabilityQueryKey(selectedCoopId);
                    return base === key[0] && cooperative_id === key[1];
                },
                availabilityId,
            ),
        initialDataUpdatedAt: moment().subtract({ minutes: 2 }).valueOf(),
        enabled: isFocused && !!availabilityId,
    });
};

type GeneralMutateParkingSharingBody = {
    content: string;
    start_at: string;
    end_at: string;
    cooperative_group_id: number | null;
};

type MutateParkingSharingRequestBody = GeneralMutateParkingSharingBody & {
    requested_availability_slot_id: string | null;
};

type MutateParkingSharingAvailabilityBody = GeneralMutateParkingSharingBody & {
    request_for_approval_id: string | null;
};

const useCreateParkingSharingRequest = (): UseMutationResult<
    { message: string; parking_sharing_request_id: string },
    string | Error | AxiosError<{ message?: string }>,
    MutateParkingSharingRequestBody
> => {
    const selectedCoopId = useSelectedCoop();
    const getAuthHeader = useGetAccessTokenHeader();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (request) => {
            const result = await axios.post<{ message: string; parking_sharing_request_id: string }>(
                `cooperatives/${selectedCoopId}/parking_sharing/availability_requests`,
                request,
                {
                    headers: { authorization: await getAuthHeader() },
                },
            );
            if (!result.data.parking_sharing_request_id) {
                throw new Error('Parking sharing request creation return unsuccessful result');
            }
            return result.data;
        },
        onSuccess: () => {
            invalidateParkingSharingQueries(queryClient);
        },
    });
};

const useCreateParkingSharingAvailability = (): UseMutationResult<
    { message: string; parking_sharing_availability_id: string },
    string | Error | AxiosError<{ message?: string }>,
    MutateParkingSharingAvailabilityBody
> => {
    const selectedCoopId = useSelectedCoop();
    const getAuthHeader = useGetAccessTokenHeader();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (availability) => {
            const result = await axios.post<{ message: string; parking_sharing_availability_id: string }>(
                `cooperatives/${selectedCoopId}/parking_sharing/availability_slots`,
                availability,
                {
                    headers: { authorization: await getAuthHeader() },
                },
            );
            if (!result.data.parking_sharing_availability_id) {
                throw new Error('Parking sharing availability creation return unsuccessful result');
            }
            return result.data;
        },
        onSuccess: () => {
            invalidateParkingSharingQueries(queryClient);
        },
    });
};

const useEditParkingSharingRequest = (): UseMutationResult<
    { message: string; parking_sharing_request_id: string },
    string | Error | AxiosError<{ message?: string }>,
    [string, MutateParkingSharingRequestBody]
> => {
    const selectedCoopId = useSelectedCoop();
    const getAuthHeader = useGetAccessTokenHeader();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ([id, body]) => {
            const result = await axios.patch<{ message: string; parking_sharing_request_id: string }>(
                `cooperatives/${selectedCoopId}/parking_sharing/availability_requests/${id}`,
                body,
                {
                    headers: { authorization: await getAuthHeader() },
                },
            );
            if (!result.data.parking_sharing_request_id) {
                throw new Error('Parking sharing request edit return unsuccessful result');
            }
            return result.data;
        },
        onSuccess: () => {
            invalidateParkingSharingQueries(queryClient);
        },
    });
};

const useEditParkingSharingAvailability = (): UseMutationResult<
    { message: string; parking_sharing_availability_id: string },
    string | Error | AxiosError<{ message?: string }>,
    [string, MutateParkingSharingAvailabilityBody]
> => {
    const selectedCoopId = useSelectedCoop();
    const getAuthHeader = useGetAccessTokenHeader();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ([id, body]) => {
            const result = await axios.patch<{ message: string; parking_sharing_availability_id: string }>(
                `cooperatives/${selectedCoopId}/parking_sharing/availability_slots/${id}`,
                body,
                {
                    headers: { authorization: await getAuthHeader() },
                },
            );
            if (!result.data.parking_sharing_availability_id) {
                throw new Error('Parking sharing availability edit return unsuccessful result');
            }
            return result.data;
        },
        onSuccess: () => {
            invalidateParkingSharingQueries(queryClient);
        },
    });
};

const useDeleteParkingSharingRequest = (): UseMutationResult<
    { message: string },
    string | Error | AxiosError<{ message?: string }>,
    string
> => {
    const selectedCoopId = useSelectedCoop();
    const getAuthHeader = useGetAccessTokenHeader();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (requestId: string) => {
            const result = await axios.delete<{ message: string }>(
                `cooperatives/${selectedCoopId}/parking_sharing/availability_requests/${requestId}`,
                {
                    headers: { authorization: await getAuthHeader() },
                },
            );
            if (!result.data.message) {
                throw new Error('Parking sharing request deletion return unsuccessful result');
            }
            return result.data;
        },
        onSuccess: () => {
            invalidateParkingSharingQueries(queryClient);
        },
    });
};

const useDeleteParkingSharingAvailability = (): UseMutationResult<
    { message: string },
    string | Error | AxiosError<{ message?: string }>,
    string
> => {
    const selectedCoopId = useSelectedCoop();
    const getAuthHeader = useGetAccessTokenHeader();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (availabilityId: string) => {
            const result = await axios.delete<{ message: string }>(
                `cooperatives/${selectedCoopId}/parking_sharing/availability_slots/${availabilityId}`,
                {
                    headers: { authorization: await getAuthHeader() },
                },
            );
            if (!result.data.message) {
                throw new Error('Parking sharing availability deletion return unsuccessful result');
            }
            return result.data;
        },
        onSuccess: () => {
            invalidateParkingSharingQueries(queryClient);
        },
    });
};

export {
    useGetParkingSharingRequests,
    useGetParkingSharingRequest,
    useGetParkingSharingAvailabilities,
    useGetParkingSharingAvailability,
    useCreateParkingSharingRequest,
    useCreateParkingSharingAvailability,
    useEditParkingSharingRequest,
    useEditParkingSharingAvailability,
    useDeleteParkingSharingRequest,
    useDeleteParkingSharingAvailability,
};
